import { NoticesComponentModule } from '@b2b/common/components/notices';
import { TreeDirectiveModule } from '@b2b/common/directives/tree/tree.directive';
import { FactoryFormsModule } from '@b2b/common/factories/forms/forms.factory';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { ApiService } from '@b2b/common/services/api/types';
import { SelectionTreeNode, TreeNodeCtrl } from '@b2b/common/utils/tree-node';
import { ACMEListResponse, ACMEResponse, ContentHeaderProps, TODO, WrappedACMEErrorResponse } from '@b2b/types/core';
import { FormDetail, FormResponse } from '@b2b/types/forms';
import { MembershipLifecycleActionForm, MembershipListData } from '@b2b/types/membership';
import uiRouter, { StateParams, StateService } from '@uirouter/angularjs';
import angular, { IScope, ITimeoutService } from 'angular';
import { PricingCheckboxModule } from '../../../pricing/discounts/components/pricing-checkbox/pricing-checkbox.component';

class MembershipFormsCreateController {
  static $inject = [
    '$scope',
    '$state',
    '$stateParams',
    '$timeout',
    'NoticeFactory',
    'FormsFactory',
    'ApiService',
    'MembershipLifecycleActionFormListData',
    'FormsListData',
    'MembershipsListData',
  ];

  notice = new this.noticeFactory();
  model: MembershipLifecycleActionForm | null = null;
  forms: FormResponse[];
  formSelected: FormDetail | null = null;
  currentForm: FormResponse;

  preferences = {
    b2c: { saleChannel: 'online', displayForm: true, formResponseRequired: true },
    pos: { saleChannel: 'pos', displayForm: true, formResponseRequired: false },
    kiosk: { saleChannel: 'kiosk', displayForm: true, formResponseRequired: false },
    insideSalesIndividual: { saleChannel: 'insidesalesindividual', displayForm: true, formResponseRequired: false },
  } as TODO;

  membershipTypesOptions = [
    { label: 'Gift', value: 'gift', checked: true },
    { label: 'Standard', value: 'standard', checked: true },
  ];

  membershipTypesSelected: TODO;

  membershipLevels: TODO[];
  tree = new SelectionTreeNode();

  constructor(
    private $scope: IScope & { membershipFormsCreate: TODO },
    private $state: StateService,
    private $stateParams: StateParams,
    private $timeout: ITimeoutService,
    private noticeFactory: NoticeFactory,
    private formsFactory: TODO,
    private apiService: ApiService,
    private MembershipLifecycleActionFormListData: { data: ACMEListResponse<MembershipLifecycleActionForm> },
    private FormsListData: { data: ACMEListResponse<FormResponse> },
    private MembershipsListData: { data: ACMEListResponse<MembershipListData> },
  ) {
    $scope.membershipFormsCreate = this;
  }

  $onInit() {
    this.$scope.$emit('Application::contentTitleChange', {
      title: this.$stateParams.id ? 'Edit Membership Form Mapping' : 'New Membership Form Mapping',
      buttonLeft: {
        label: 'Close',
        action: () => {
          this.close();
        },
      },
      buttonRight: {
        label: 'Save',
        action: () => {
          this.createForm();
        },
      },
    } satisfies ContentHeaderProps);

    if (this.FormsListData && this.FormsListData.data.success) {
      this.forms = this.FormsListData.data.payload.list;
    }

    if (this.MembershipsListData && this.MembershipsListData.data.success) {
      this.membershipLevels = this.MembershipsListData.data.payload.list;
    }

    if (
      this.$stateParams.id &&
      this.MembershipLifecycleActionFormListData &&
      this.MembershipLifecycleActionFormListData.data.success
    ) {
      // Kiosk and Inside Sales Individual are set to false 
      // for previous forms configured before the addition of these sale channels 
      // in order to avoid showing the form in those channels without the admin explicitly enabling it,
      // so we set displayForm to false for both of them when loading the form mapping details
      this.preferences.kiosk.displayForm = false;
      this.preferences.insideSalesIndividual.displayForm = false;

      // Load the form mapping details
      this.setForm(this.MembershipLifecycleActionFormListData.data.payload.list);
    }
  }

  close() {
    this.$state.go(
      'application.membership.forms',
      { action: this.$stateParams.action },
      { notify: true, reload: true, inherit: false },
    );
  }

  setForm(membershipLifecycleActionFormList: MembershipLifecycleActionForm[]) {
    const stateFormId = this.$stateParams.id;
    this.model = membershipLifecycleActionFormList.find((item) => item.id === stateFormId) ?? null;
    this.formSelected = this.forms.find((item) => item.id === this.model?.form.id) || null;

    const b2c = this.model?.formPreferences.find((item) => item.saleChannel.toLocaleLowerCase() === 'online');
    this.preferences.b2c = b2c ?? this.preferences.b2c;

    const pos = this.model?.formPreferences.find((item) => item.saleChannel.toLocaleLowerCase() === 'pos');
    this.preferences.pos = pos ?? this.preferences.pos;

    const kiosk = this.model?.formPreferences.find((item) => item.saleChannel.toLocaleLowerCase() === 'kiosk');
    this.preferences.kiosk = kiosk ?? this.preferences.kiosk;

    const insideSalesIndividual = this.model?.formPreferences.find((item) => item.saleChannel.toLocaleLowerCase() === 'insidesalesindividual' || item.saleChannel.toLocaleLowerCase() === 'inside_sales_individual');
    this.preferences.insideSalesIndividual = {
      ...this.preferences.insideSalesIndividual,
      displayForm: insideSalesIndividual ? insideSalesIndividual.displayForm : this.preferences.insideSalesIndividual.displayForm,
      formResponseRequired: insideSalesIndividual ? insideSalesIndividual.formResponseRequired : this.preferences.insideSalesIndividual.formResponseRequired,
    }
    
    this.setMembershipLevelsInfo();

    if (this.model) {
      const [gift, standard] = this.membershipTypesOptions;
      gift.checked = this.model.displayFormForGiftPurchase;
      standard.checked = this.model.displayFormForStandardPurchase;
    }

    this.$timeout(() => {
      // We have to wait until the next tick because TreeNodes haven't been created on the DOM yet
      this.tree.updateTree();
    }, 0);

    this.onSelectForm();
  }

  toggleTree() {
    this.tree.toggleTree();
  }

  toggleNode(
    $ctrl: TreeNodeCtrl<{ selected: boolean; partial: boolean }>,
    nodeModel: { selected: boolean; partial: boolean },
  ) {
    this.tree.toggleNode($ctrl, nodeModel);
  }

  createForm() {
    this.notice.hideMessages();

    if (!this.formSelected) {
      this.notice.showErrorMessages([{ message: 'Please choose a form.' }], { duration: 3000 });
      return;
    }

    const lifecycleAction = this.$stateParams.action;
    const form = this.formSelected;
    const displayFormForGiftPurchase = !!this.membershipTypesSelected.find((item) => item.value === 'gift');
    const displayFormForStandardPurchase = !!this.membershipTypesSelected.find((item) => item.value === 'standard');
    const formPreferences = [this.preferences.b2c, this.preferences.pos, this.preferences.kiosk, this.preferences.insideSalesIndividual];
    const membershipLevelsInfo = this.getMembershipLevelsInfo();

    if (membershipLevelsInfo.length) {
      if (this.model?.id) {
        this.apiService.membership.lifecycle.forms
          .update({
            id: this.model.id,
            lifecycleAction,
            form,
            displayFormForGiftPurchase,
            displayFormForStandardPurchase,
            formPreferences,
            membershipLevelsInfo,
          })
          .then((response) => this.onSuccessCreate(response))
          .catch((response) => this.onAPICallError(response));
      } else {
        this.apiService.membership.lifecycle.forms
          .create({
            lifecycleAction,
            form,
            displayFormForGiftPurchase,
            displayFormForStandardPurchase,
            formPreferences,
            membershipLevelsInfo,
          })
          .then((response) => this.onSuccessCreate(response))
          .catch((response) => this.onAPICallError(response));
      }
    } else {
      this.notice.showErrorMessages([{ message: 'Please choose at least one membership offering' }], {
        duration: 3000,
      });
    }
  }

  hasForms() {
    return Array.isArray(this.forms) && this.forms.length;
  }

  hasFormsSelected() {
    return !!this.formSelected;
  }

  getMembershipLevelsInfo() {
    const memberships: TODO = [];
    let offerings: TODO = [];

    for (let i = 0, ii = this.membershipLevels.length; i < ii; i++) {
      if (this.membershipLevels[i].selected || this.membershipLevels[i].partial) {
        offerings = [];

        for (let j = 0, jj = this.membershipLevels[i].offerings.length; j < jj; j++) {
          if (this.membershipLevels[i].offerings[j].selected) {
            offerings.push({
              id: this.membershipLevels[i].offerings[j].id,
              name: this.membershipLevels[i].offerings[j].name,
            });
          }
        }

        if (offerings.length) {
          memberships.push({
            membershipLevel: {
              id: this.membershipLevels[i].id,
              name: this.membershipLevels[i].name,
            },
            membershipOfferings: offerings,
          });
        }
      }
    }

    return memberships;
  }

  setMembershipLevelsInfo() {
    for (let i = 0, ii = this.model?.membershipLevelsInfo.length || 0; i < ii; i++) {
      const membershipLevel = this.model?.membershipLevelsInfo[i];

      if (Array.isArray(membershipLevel?.membershipOfferings) && membershipLevel.membershipOfferings.length) {
        const found = this.membershipLevels.find((item) => item.id === membershipLevel.membershipLevel.id);

        if (found && Array.isArray(found.offerings) && found.offerings.length) {
          for (let j = 0, jj = membershipLevel.membershipOfferings.length; j < jj; j++) {
            const foundOffering = found.offerings.find((item) => item.id === membershipLevel.membershipOfferings[j].id);
            if (foundOffering) {
              foundOffering.selected = true;
            }
          }

          this.updateMembershipLevelSelection(found);
        }
      }
    }
  }

  updateMembershipLevelSelection(level: TODO) {
    let selected: TODO[] = [];

    level.partial = false;
    level.selected = false;

    if (level && Array.isArray(level.offerings)) {
      selected = level.offerings.filter((offering) => offering.selected);

      if (selected.length) {
        if (selected.length < level.offerings.length) {
          level.partial = true;
        } else if (selected.length === level.offerings.length) {
          level.selected = true;
          level.partial = false;
        }
      }
    }
  }

  hasPreview() {
    return this.formSelected ? JSON.parse(this.formSelected.sourceFormDefinition).length > 3 : false;
  }

  previewForm() {
    new this.formsFactory().previewForm({
      formId: this.formSelected?.id,
    });
  }

  showSuccessMessage() {
    return this.notice.showSuccessMessages([{ message: 'Membership form mapping saved successfully.' }], {
      duration: 2000,
    });
  }

  redirectToEditForm() {
    this.$state.go('application.membership.forms.edit', {
      id: this.model?.id,
      action: this.$stateParams.action,
      success: true,
    });
  }

  onSuccessCreate(response: { data: ACMEResponse<TODO> }) {
    this.model = response.data.payload;
    this.showSuccessMessage();
    this.redirectToEditForm();
  }

  onAPICallError(response: { data: WrappedACMEErrorResponse }) {
    this.notice.showErrorMessages([{ message: response.data.error }], { duration: 3000 });
  }

  onSelectForm() {
    const sourceFormDefinition = this.formSelected
      ? JSON.stringify(JSON.parse(this.formSelected.sourceFormDefinition).slice(0, 3))
      : '[]';

    this.$timeout(() => {
      new this.formsFactory().renderForm({
        containerId: 'templates-create-schedules-create-forms-preview-form',
        customFields: [],
        sourceFormDefinition,
      });
    }, 100);
  }
}

export const MembershipFormsCreateModule = angular
  .module('acme.view.application.membership.forms.create.controllers', [
    uiRouter,
    'acme.common.factory.forms',
    'acme.common.factory.notice',
    'acme.common.service.api',
    FactoryFormsModule,
    TreeDirectiveModule,
    NoticesComponentModule,
    PricingCheckboxModule,
  ])
  .controller('MembershipFormsCreateController', MembershipFormsCreateController).name;
